import { Link } from "react-router-dom";
import { Check, ArrowRight, Phone, Video, BadgeDollarSign } from "lucide-react";
import { SERVICES, BUSINESS } from "@/lib/business";

export default function Services() {
  return (
    <div data-testid="services-page" className="bg-slate-50">
      {/* HEADER */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="text-xs tracking-[0.22em] uppercase font-semibold text-cyan-400">
            Capabilities
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-4 max-w-4xl">
            Three trades. Built for the businesses of {BUSINESS.serviceArea}.
          </h1>
          <p className="mt-6 text-slate-300 max-w-2xl leading-relaxed">
            Below is a snapshot of the equipment we service. Don't see what you have?
            Call us — we likely fix it.
          </p>
          <div className="mt-8 flex flex-wrap gap-3" data-testid="services-pricing-badges">
            <span className="inline-flex items-center gap-2 border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 px-4 py-2 text-xs tracking-[0.18em] uppercase font-bold">
              <Video className="w-3.5 h-3.5" /> Free phone &amp; video consult
            </span>
            <span className="inline-flex items-center gap-2 border border-white/20 bg-white/5 text-white px-4 py-2 text-xs tracking-[0.18em] uppercase font-bold">
              <BadgeDollarSign className="w-3.5 h-3.5" /> $65 service call
            </span>
          </div>
        </div>
      </section>

      {/* SERVICES LIST */}
      <section>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 space-y-20">
          {SERVICES.map((s, idx) => {
            const reverse = idx % 2 === 1;
            return (
              <div
                key={s.id}
                data-testid={`service-detail-${s.id}`}
                className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center"
              >
                <div
                  className={`md:col-span-7 ${
                    reverse ? "md:order-2 md:col-start-6" : ""
                  }`}
                >
                  <div className="aspect-[16/10] overflow-hidden border border-slate-200">
                    <img
                      src={s.image}
                      alt={s.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div className={`md:col-span-5 ${reverse ? "md:order-1" : ""}`}>
                  <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-blue-900">
                    {s.label} / {s.short}
                  </div>
                  <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl text-slate-900 mt-4">
                    {s.title}
                  </h2>
                  <p className="text-base text-slate-600 leading-relaxed mt-5">
                    {s.description}
                  </p>
                  <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="w-4 h-4 mt-0.5 text-blue-900 shrink-0" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8 flex flex-wrap gap-3">
                    <Link
                      to="/contact"
                      data-testid={`service-${s.id}-request`}
                      className="inline-flex items-center gap-2 bg-blue-900 hover:bg-blue-800 text-white px-6 py-3 text-sm font-semibold tracking-wide uppercase transition-colors"
                    >
                      Request service <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a
                      href={BUSINESS.phoneHref}
                      data-testid={`service-${s.id}-call`}
                      className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-900 text-slate-900 px-6 py-3 text-sm font-semibold tracking-wide uppercase transition-colors"
                    >
                      <Phone className="w-4 h-4" /> Call
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="bg-cyan-500 text-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <div className="text-[10px] tracking-[0.22em] uppercase font-bold">Ready when you are</div>
            <h3 className="font-display font-black text-2xl sm:text-3xl mt-2">
              Down equipment is lost revenue. Let's get you running.
            </h3>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/contact"
              data-testid="services-cta-contact"
              className="inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-white px-7 py-4 text-sm font-semibold tracking-wide uppercase transition-colors"
            >
              Get a repair scheduled <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
